// src/app/modules/demandes/UsersPickerModal.jsx
import React, { useEffect, useMemo, useState } from "react";
import "../../RessourcesStyles/UsersListModal.css";

const PAGE_SIZE = 15;

const norm = (v) => String(v ?? "").trim().toLowerCase();

function buildLabel(u) {
    const first = u?.firstName || u?.prenom || "";
    const last = u?.lastName || u?.nom || "";
    return `${first} ${last}`.trim() || u?.username || u?.login || `User#${u?.id ?? ""}`;
}

export default function UsersPickerModal({
                                             open,
                                             onClose,
                                             users = [],
                                             title = "Sélectionner des utilisateurs",
                                             onConfirm, // (selectedIds:number[]) => void
                                         }) {
    const [search, setSearch] = useState("");
    const [page, setPage] = useState(1);
    const [selected, setSelected] = useState(() => new Set());

    useEffect(() => {
        if (!open) return;
        setSearch("");
        setPage(1);
        setSelected(new Set());
    }, [open]);

    const filtered = useMemo(() => {
        const list = Array.isArray(users) ? users : [];
        const q = norm(search);
        if (!q) return list;

        return list.filter((u) => {
            const name = norm(buildLabel(u));
            const matricule = norm(u?.matricule);
            const email = norm(u?.email || u?.mail);
            const entity = norm(u?.entityName || u?.entiteNom || u?.etablissementNom || u?.companyName);
            return name.includes(q) || matricule.includes(q) || email.includes(q) || entity.includes(q);
        });
    }, [users, search]);

    const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
    const pageSafe = Math.min(Math.max(page, 1), totalPages);
    const start = (pageSafe - 1) * PAGE_SIZE;
    const pageItems = filtered.slice(start, start + PAGE_SIZE);

    const selectedCount = selected.size;
    const pageIds = useMemo(() => pageItems.map((u) => u?.id).filter((id) => id != null), [pageItems]);
    const allPageSelected = pageIds.length > 0 && pageIds.every((id) => selected.has(id));
    const somePageSelected = pageIds.some((id) => selected.has(id));

    const toggle = (id) => {
        if (id == null) return;
        setSelected((prev) => {
            const next = new Set(prev);
            if (next.has(id)) next.delete(id);
            else next.add(id);
            return next;
        });
    };

    const toggleAllPage = () => {
        setSelected((prev) => {
            const next = new Set(prev);
            if (allPageSelected) pageIds.forEach((id) => next.delete(id));
            else pageIds.forEach((id) => next.add(id));
            return next;
        });
    };

    const clearSelection = () => setSelected(new Set());

    const confirm = () => {
        const ids = Array.from(selected.values());
        onConfirm?.(ids);
    };

    if (!open) return null;

    return (
        <div className="modalOverlay" onMouseDown={onClose}>
            <div className="modalCard" onMouseDown={(e) => e.stopPropagation()}>
                <div className="modalHeader">
                    <div>
                        <div className="modalTitle">{title}</div>
                        <div className="modalSubTitle">
                            {filtered.length} utilisateur(s) — {selectedCount} sélectionné(s)
                        </div>
                    </div>
                    <button className="btn btn-secondary" type="button" onClick={onClose}>
                        Fermer
                    </button>
                </div>

                <div className="modalBody">
                    <div className="campagne-grid-3" style={{ marginBottom: 12 }}>
                        <div className="field" style={{ gridColumn: "1 / span 2" }}>
                            <label>Recherche</label>
                            <input
                                value={search}
                                onChange={(e) => {
                                    setSearch(e.target.value);
                                    setPage(1);
                                }}
                                placeholder="Nom, matricule, email, entité..."
                            />
                        </div>

                        <div className="field">
                            <label>Actions</label>
                            <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
                                <button className="btn btn-secondary" type="button" onClick={toggleAllPage} disabled={pageIds.length === 0}>
                                    {allPageSelected ? "Désélectionner (page)" : "Tout sélectionner (page)"}
                                </button>
                                <button className="btn btn-secondary" type="button" onClick={clearSelection} disabled={selectedCount === 0}>
                                    Réinitialiser
                                </button>
                            </div>
                        </div>
                    </div>

                    <div className="tableWrap usersPickWrap">
                        <table className="table">
                            <thead className="stickyHead">
                            <tr>
                                <th style={{ width: 46 }}>
                                    <input
                                        type="checkbox"
                                        checked={allPageSelected}
                                        ref={(el) => {
                                            if (!el) return;
                                            el.indeterminate = !allPageSelected && somePageSelected;
                                        }}
                                        onChange={toggleAllPage}
                                        aria-label="Sélectionner la page"
                                    />
                                </th>
                                <th>Utilisateur</th>
                                <th>Matricule</th>
                                <th>Email</th>
                                <th>Entité</th>
                            </tr>
                            </thead>

                            <tbody>
                            {pageItems.length === 0 ? (
                                <tr>
                                    <td colSpan={5} style={{ padding: 16, color: "#6b7280" }}>
                                        Aucun utilisateur trouvé.
                                    </td>
                                </tr>
                            ) : (
                                pageItems.map((u) => {
                                    const id = u?.id;
                                    const checked = selected.has(id);
                                    return (
                                        <tr key={id ?? buildLabel(u)}>
                                            <td>
                                                <input
                                                    type="checkbox"
                                                    checked={checked}
                                                    onChange={() => toggle(id)}
                                                    aria-label={`Sélectionner ${buildLabel(u)}`}
                                                />
                                            </td>
                                            <td style={{ fontWeight: 700, color: "#111827" }}>{buildLabel(u)}</td>
                                            <td>{u?.matricule || "—"}</td>
                                            <td>{u?.email || u?.mail || "—"}</td>
                                            <td>{u?.entityName || u?.entiteNom || u?.etablissementNom || u?.companyName || "—"}</td>
                                        </tr>
                                    );
                                })
                            )}
                            </tbody>
                        </table>
                    </div>

                    <div className="paginationRow">
                        <button className="btn btn-secondary" type="button" onClick={() => setPage((p) => Math.max(1, p - 1))} disabled={pageSafe <= 1}>
                            Précédent
                        </button>
                        <div className="muted">
                            Page {pageSafe} / {totalPages}
                        </div>
                        <button className="btn btn-secondary" type="button" onClick={() => setPage((p) => Math.min(totalPages, p + 1))} disabled={pageSafe >= totalPages}>
                            Suivant
                        </button>
                    </div>
                </div>

                <div className="modalHeader" style={{ borderTop: "1px solid #e5e7eb", borderBottom: "none" }}>
                    <button className="btn btn-secondary" type="button" onClick={onClose}>
                        Annuler
                    </button>
                    <button className="btn btn-primary" type="button" onClick={confirm} disabled={selectedCount === 0}>
                        Confirmer ({selectedCount})
                    </button>
                </div>
            </div>
        </div>
    );
}
